import {
  AdminDataTable,
  AdminTableIdentity,
  AdminTableRowMenu,
} from "@bednbody/ui/components/admin-data-table";
import { Badge } from "@bednbody/ui/components/badge";
import { Callout } from "@bednbody/ui/components/callout";
import { RetryButton } from "@bednbody/ui/components/retry-button";
import type { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import {
  type StaffEditKind,
  StaffEditDialogs,
  rankLabel,
} from "../roster/components/StaffEditDialogs";
import type { RosterPerson } from "../roster/components/types";
import { useRosterStaff } from "../roster/roster.hooks";
import { type TierScore, TIERS, useLatestTierScores } from "./rankings.hooks";

/**
 * Tab 1 — who holds which rank right now.
 *
 * The rank column is the rank in force (`staff.therapist_rank` / `rider_rank`),
 * NOT the latest score's band: a hand-set rank outranks the computed one until a
 * human releases it (0329). The score columns show what the system last said, so
 * the two can be compared. Changing a rank reuses the roster's rank dialog — the
 * audited Edge route and the release-to-system action live there, not here.
 */

interface RankRow {
  person: RosterPerson;
  role: "therapist" | "rider";
  rank: string;
  manual: boolean;
  score: TierScore | null;
}

const monthLabel = (month: string) =>
  new Date(`${month}T00:00:00`).toLocaleDateString("en-PH", { month: "short", year: "numeric" });

export function CurrentRanksTab() {
  const staff = useRosterStaff(null);
  const scores = useLatestTierScores();
  // The person outlives the dialog: the dialog fades out, and unmounting it with
  // the person would cut that animation (frontend-standards §6).
  const [person, setPerson] = useState<RosterPerson | null>(null);
  const [kind, setKind] = useState<StaffEditKind | null>(null);

  const rows = useMemo<RankRow[]>(
    () =>
      (staff.data ?? [])
        .filter((p) => p.role === "therapist" || p.role === "rider")
        .map((person) => {
          const role = person.role === "therapist" ? ("therapist" as const) : ("rider" as const);
          return {
            person,
            role,
            rank: (role === "therapist" ? person.therapist_rank : person.rider_rank) ?? "bronze",
            manual: person.rank_is_manual,
            score: scores.data?.get(person.id) ?? null,
          };
        }),
    [staff.data, scores.data],
  );

  const columns = useMemo<ColumnDef<RankRow, unknown>[]>(
    () => [
      {
        id: "person",
        accessorFn: (row) => row.person.name,
        header: "Person",
        cell: ({ row }) => (
          <AdminTableIdentity
            title={row.original.person.name}
            subtitle={row.original.role === "therapist" ? "Therapist" : "Rider"}
            fallback={row.original.person.name.charAt(0)}
          />
        ),
      },
      {
        id: "rank",
        accessorFn: (row) => TIERS.indexOf(row.rank as (typeof TIERS)[number]),
        header: "Rank",
        cell: ({ row }) => (
          <Badge variant={row.original.rank === "diamond" ? "gold" : "muted"}>
            {rankLabel(row.original.rank)}
          </Badge>
        ),
      },
      {
        id: "score",
        accessorFn: (row) => row.score?.total_score ?? -1,
        header: "Latest score",
        cell: ({ row }) =>
          row.original.score ? (
            <span className="tabular-nums">
              {row.original.score.total_score.toFixed(2)}
              <span className="ml-2 text-xs text-muted-foreground">
                {row.original.score.is_complete
                  ? rankLabel(row.original.score.tier)
                  : "partial — not ranked"}
              </span>
            </span>
          ) : (
            <span className="text-muted-foreground">No score yet</span>
          ),
      },
      {
        id: "month",
        accessorFn: (row) => row.score?.cycle_month ?? "",
        header: "Month",
        cell: ({ row }) => (row.original.score ? monthLabel(row.original.score.cycle_month) : "—"),
      },
      {
        id: "source",
        accessorFn: (row) => (row.manual ? "manual" : "system"),
        header: "Set by",
        cell: ({ row }) =>
          row.original.manual ? (
            <Badge variant="warning">Manual override</Badge>
          ) : (
            <Badge variant="success-soft">Monthly system</Badge>
          ),
      },
      {
        id: "actions",
        enableSorting: false,
        header: "",
        cell: ({ row }) => (
          <AdminTableRowMenu
            label={row.original.person.name}
            actions={[
              {
                label: row.original.manual ? "Change or release rank…" : "Change rank…",
                onSelect: () => {
                  setPerson(row.original.person);
                  setKind(row.original.role === "therapist" ? "therapist-rank" : "rider-rank");
                },
              },
            ]}
          />
        ),
      },
    ],
    [],
  );

  if (staff.isError || scores.isError) {
    return (
      <div className="space-y-3 p-4">
        <p className="text-sm text-muted-foreground">
          Ranks could not be loaded. That is a failed read, not an empty list.
        </p>
        <RetryButton size="sm" onRetry={() => Promise.all([staff.refetch(), scores.refetch()])} />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <Callout title="Current ranks">
        <p>
          The rank in force. A manual override outranks the monthly score until it is released. The
          first automatic run is <span className="font-semibold">1 Nov, Manila time.</span>
        </p>
      </Callout>

      <AdminDataTable
        presentation="standalone"
        data={rows}
        columns={columns}
        getRowId={(row) => row.person.id}
        loading={staff.isLoading || scores.isLoading}
        searchText={(row) => `${row.person.name} ${row.role} ${row.rank}`}
        searchPlaceholder="Search by name…"
        exportFilename="current-ranks"
        exportColumns={[
          { header: "Name", value: (row) => row.person.name },
          { header: "Role", value: (row) => row.role },
          { header: "Rank", value: (row) => row.rank },
          { header: "Latest score", value: (row) => row.score?.total_score },
          { header: "Score month", value: (row) => row.score?.cycle_month },
          { header: "Manual override", value: (row) => (row.manual ? "yes" : "no") },
        ]}
        facets={[
          {
            id: "role",
            label: "Role",
            options: [
              { value: "therapist", label: "Therapists" },
              { value: "rider", label: "Riders" },
            ],
            getValue: (row) => row.role,
          },
          {
            id: "rank",
            label: "Rank",
            options: TIERS.map((tier) => ({ value: tier, label: rankLabel(tier) })),
            getValue: (row) => row.rank,
          },
          {
            id: "source",
            label: "Set by",
            options: [
              { value: "manual", label: "Manual override" },
              { value: "system", label: "Monthly system" },
            ],
            getValue: (row) => (row.manual ? "manual" : "system"),
          },
        ]}
        emptyTitle="No therapists or riders"
        emptyDescription="Ranks appear here once staff are on the roster."
      />
      {person ? <StaffEditDialogs person={person} dialog={kind} onDialogChange={setKind} /> : null}
    </div>
  );
}
